// client/src/features/contracts/search.ts — SCR-CON-001's URL state · CON-005 (04 §9.2.3).
//
// §9.2.3 is NORMATIVE for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered list is shareable, survives refresh, and browser
// Back is a filter-undo. `features/tenants/search.ts` is the shape this copies.
//
// The names are `GET /api/v1/contracts`'s own — `status`, `propertyId`, `tenantId`,
// `expiringSoon`, `q` plus the 02 §7.4 paging — so the URL of the screen and the query string
// of the request are the same vocabulary, and `toListQuery` is a rename-free pass-through.

import { z } from 'zod';

import { CONTRACT_STATUSES } from './enums';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const contractSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  q: z.string().trim().optional(),
  status: z.enum(CONTRACT_STATUSES).optional(),
  propertyId: z.string().uuid().optional(),
  tenantId: z.string().uuid().optional(),
  /**
   * The derived alert, as a filter. „Expiră în ≤60 zile" is never a status (04 §4.8,
   * FOUNDATION_BRIEF §3) — `isExpiringSoon` is computed by the server from the end date and
   * today — so it is its own boolean beside `status` rather than a sixth member of it.
   */
  expiringSoon: z.coerce.boolean().optional(),
});

export type ContractSearch = z.infer<typeof contractSearchSchema>;

/** Which filters are engaged — the empty state needs to know why it is empty. */
export function hasActiveFilters(search: ContractSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.status !== undefined ||
    search.propertyId !== undefined ||
    search.tenantId !== undefined ||
    search.expiringSoon === true
  );
}

/** Clearing filters returns to page 1: page 3 of a set that no longer exists renders nothing. */
export const CLEARED_SEARCH: ContractSearch = { page: 1, pageSize: 25 };
